import { ReactComponent as ArrowSvg } from 'assets/icons/arrow.svg';
import { useMeetInfo } from './utils/useMeetInfo';
import useVideoRoom from './utils/useVideoRoom';
import { PostCall } from './postCall/PostCall';
import { useWebRtc } from './utils/useWebRTC';
import { PreCall } from './preCall/PreCall';
import { useEffect, useState, useRef } from 'react';
import { InCall } from './inCall/inCall';
import Loading from './loading/Loading';
import { State } from './types/Video';
import OT from '@vonage/client-sdk-video';
import { VonageClient } from '@vonage/client-sdk';
import { createVonageSession } from './utils/createVonageSession';
import { createVideoSessionToken } from './utils/createVideoSessionToken';
import { getVonageToken } from './utils/getVonageToken';
import { saveInteractionAttributes } from './utils/saveInteractionAttributes';
import { useCallState } from './utils/useCallState';
import { logger } from 'utils/logger';

type SipStateProps = {
  roomId: string | null;
  accountId: number;
  widgetId: string;
  widgetName: string;
  url: string;
  visitorId: string;
  sessionId?: string | null;
  trueengage_form?: any;
  scheduleVideo?: boolean;
  // Vonage voice fields
  phone?: string;
  remoteName?: string;
  type?: string;
};

const Video = () => {
  const [currentState, setCurrentState] = useState<State>('preCall');
  const { meetInfo, fetchMeetInfoLoading, fetchMeetInfoError } = useMeetInfo();
  const { webRTCClient, activeCall } = useWebRtc(meetInfo.accountId, meetInfo.webrtcProvider);
  const isUpgrade = currentState === 'upgradeDashboard';
  const isVonageProvider = meetInfo.webrtcProvider === 'vonage';

  // Vonage voice client (call signaling via @vonage/client-sdk)
  const [vonageVoiceClient, setVonageVoiceClient] = useState<VonageClient | null>(null);
  const vonageCallIdRef = useRef<string | null>(null);

  // Vonage video state
  const [vonageSession, setVonageSession] = useState<any>(null);
  const [vonagePublisher, setVonagePublisher] = useState<any>(null);
  const [vonageRemoteStream, setVonageRemoteStream] = useState<any>(null);
  const [vonageSessionId, setVonageSessionId] = useState<string | null>(null);
  const [vonageToken, setVonageToken] = useState<any>(null);
  const [vonageMicrophoneMuted, setVonageMicrophoneMuted] = useState<boolean>(false);
  const [vonageCameraMuted, setVonageCameraMuted] = useState<boolean>(false);
  const [vonageIsVideoBlurred, setVonageIsVideoBlurred] = useState<boolean>(false);
  const [frontCameraId, setFrontCameraId] = useState<string | null>(null);
  const [backCameraId, setBackCameraId] = useState<string | null>(null);
  const [cameraDevicesReady, setCameraDevicesReady] = useState<boolean>(false);
  const [isCameraRotated, setIsCameraRotated] = useState<boolean>(false);
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState<boolean>(false);
  const localVideoRef = useRef<HTMLDivElement>(null);

  const vonageSessionRef = useRef<any>(null);
  const vonagePublisherRef = useRef<any>(null);
  useEffect(() => {
    vonageSessionRef.current = vonageSession;
  }, [vonageSession]);
  useEffect(() => {
    vonagePublisherRef.current = vonagePublisher;
  }, [vonagePublisher]);

  const setSipAudioAudible = (audible: boolean) => {
    try {
      const client: any = vonageVoiceClient;
      const callId = vonageCallIdRef.current;
      if (client && callId && typeof client.getPeerConnection === 'function') {
        const pc = client.getPeerConnection(callId);
        pc?.getReceivers?.()
          .filter((r: any) => r.track?.kind === 'audio')
          .forEach((r: any) => {
            r.track.enabled = audible;
          });
      }
    } catch (e) {
      console.error('[VIDEO][audio] setSipAudioAudible error', e);
    }
  };

  const { callStatus, handleCallStateSignal, handleStreamCreated, handleStreamDestroyed } = useCallState({
    getPublisher: () => vonagePublisherRef.current,
    isCameraOn: () => !vonageCameraMuted,
    subscribe: (stream: any) =>
      vonageSessionRef.current?.subscribe(stream, 'remoteVideoInCall', {
        insertMode: 'append',
        width: '100%',
        height: '100%',
        showControls: false,
      }),
    unsubscribe: (sub: any) => {
      try {
        vonageSessionRef.current?.unsubscribe(sub);
      } catch {
        /* noop */
      }
    },
    setDisplayedRemoteStream: (s: any) => setVonageRemoteStream(s),
    setSipAudioAudible,
  });

  const blurSettings = {
    type: 'backgroundBlur',
    blurStrength: 'high',
  };

  // Use useVideoRoom only for preCall (local video preview)
  const {
    isRoomInitialized,
    room,
    roomId,
    localVideoTrack,
    localBlurredVideoTrack,
    localAudioTrack,
    remoteAudioTrack,
    remoteVideoTrack,
    availableDevices,
    selectedDevices,
    microphoneMuted,
    toggleMicrophone,
    cameraMuted,
    toggleCamera,
    applyBlurOnPreCallVideo,
    applyBlurOnInCallVideo,
    TurnCameraOnInCallVideo,
    updateDevice,
    initRoom,
    isVideoBlurred,
    sendFinalStats,
  } = useVideoRoom(isUpgrade);

  const changeCurrentState = (state: State) => {
    setCurrentState(state);
  };

  const getDeviceIdByFacingMode = async (mode: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode },
        audio: true,
      });
      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      const deviceId = settings.deviceId;

      stream.getTracks().forEach((t) => {
        t.stop();
      });

      setCameraPermissionDenied(false);
      return deviceId;
    } catch (error: any) {
      if (error?.name === 'NotAllowedError') {
        setCameraPermissionDenied(true);
      }
      console.error(`Error getting device ID for ${mode}:`, error);
      return null;
    }
  };

  const initializeCameraDeviceIds = async () => {
    try {
      const frontCamera = (await getDeviceIdByFacingMode('user')) ?? null;
      const backCamera = (await getDeviceIdByFacingMode('environment')) ?? frontCamera ?? null;
      setFrontCameraId(frontCamera);
      setBackCameraId(backCamera);
      setCameraDevicesReady(true);
    } catch (error) {
      console.error('Error initializing camera device IDs:', error);
      setCameraDevicesReady(true);
    }
  };

  const initVonagePreview = async (sessionId: string, token: any, elementId: string = 'localVideoPreCall') => {
    const applicationId = process.env.REACT_APP_VONAGE_APPLICATION_ID || '5ca20ba4-561c-42c5-b063-136f6ec614c6';
    console.log('Initializing Vonage preview for preCall:', sessionId);

    const session = OT.initSession(applicationId, sessionId);
    vonageSessionRef.current = session;
    session.on('signal:call-state', handleCallStateSignal);

    session.on('streamCreated', (event: any) => {
      console.log('Stream created:', event);
      if (session.connection && event.stream.connection.connectionId !== session.connection.connectionId) {
        handleStreamCreated(event.stream);
      }
    });

    session.on('streamDestroyed', (event: any) => {
      console.log('Stream destroyed:', event);
      handleStreamDestroyed(event.stream);
    });

    session.on('sessionConnected', (event: any) => {
      console.log('Session connected (preCall preview):', event);
      // Don't publish yet, just show preview
    });

    session.on('sessionDisconnected', (event: any) => {
      console.log('Session disconnected:', event);
    });

    const option = {
      height: '100%',
      width: '100%',
      showControls: false,
      publishAudio: false, // Don't publish audio in preview
      publishVideo: true, // Show video preview but don't publish to session yet
      insertMode: 'append' as const,
    };

    const publisher = OT.initPublisher(elementId, option, (error: any) => {
      if (error) {
        if (error.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
          setCameraPermissionDenied(true);
        }
        console.error('Error initializing publisher for preview:', error);
      } else {
        setCameraPermissionDenied(false);
        console.log('Publisher initialized successfully for preview');
        console.log('Publisher element:', publisher.element);
      }
    });

    setVonageSession(session);
    setVonagePublisher(publisher);

    const tokenValue = token.token || token;
    console.log('Connecting with token for preview:', tokenValue);
    session.connect(tokenValue, (error: any) => {
      if (error) {
        console.error('Error connecting to session:', error);
      } else {
        console.log('Successfully connected to session for preview');
      }
    });
  };

  const initVonage = async (sessionId: string, token: any) => {
    // If publisher already exists from preCall, just publish it
    if (vonagePublisher && vonageSession) {
      console.log('Using existing publisher from preCall');
      vonageSession.publish(vonagePublisher, (error: any) => {
        if (error) {
          console.error('Error publishing existing publisher:', error);
        } else {
          console.log('Successfully published existing publisher');
        }
      });
      return;
    }

    // Otherwise initialize new session (fallback)
    const applicationId = process.env.REACT_APP_VONAGE_APPLICATION_ID || '5ca20ba4-561c-42c5-b063-136f6ec614c6';
    console.log('Initializing new Vonage session:', sessionId);

    const session = OT.initSession(applicationId, sessionId);
    vonageSessionRef.current = session;
    session.on('signal:call-state', handleCallStateSignal);

    session.on('streamCreated', (event: any) => {
      console.log('Stream created:', event);
      if (session.connection && event.stream.connection.connectionId !== session.connection.connectionId) {
        handleStreamCreated(event.stream);
      }
    });

    session.on('streamDestroyed', (event: any) => {
      console.log('Stream destroyed:', event);
      handleStreamDestroyed(event.stream);
    });

    session.on('sessionConnected', (event: any) => {
      console.log('Session connected:', event);
      // Publikuj lokalny stream
      session.publish(publisher, (error: any) => {
        if (error) {
          console.error('Error publishing:', error);
        } else {
          console.log('Successfully published');
        }
      });
    });

    session.on('sessionDisconnected', (event: any) => {
      console.log('Session disconnected:', event);
    });

    const option = {
      height: '100%',
      width: '100%',
      showControls: false,
      publishAudio: !vonageMicrophoneMuted,
      publishVideo: true,
      insertMode: 'append' as const,
    };

    const publisher = OT.initPublisher('localVideoInCall', option, (error: any) => {
      if (error) {
        if (error.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
          setCameraPermissionDenied(true);
        }
        console.error('Error initializing publisher:', error);
      } else {
        setCameraPermissionDenied(false);
        console.log('Publisher initialized successfully');
        console.log('Publisher element:', publisher.element);
      }
    });

    setVonageSession(session);
    setVonagePublisher(publisher);

    const tokenValue = token.token || token;
    console.log('Connecting with token:', tokenValue);
    session.connect(tokenValue, (error: any) => {
      if (error) {
        console.error('Error connecting to session:', error);
      } else {
        console.log('Successfully connected to session');
      }
    });
  };

  const vonageToggleMicrophone = () => {
    const callId = vonageCallIdRef.current;
    if (vonageMicrophoneMuted) {
      vonagePublisher?.publishAudio(true);
      if (vonageVoiceClient && callId) vonageVoiceClient.unmute(callId);
      setVonageMicrophoneMuted(false);
    } else {
      vonagePublisher?.publishAudio(false);
      if (vonageVoiceClient && callId) vonageVoiceClient.mute(callId);
      setVonageMicrophoneMuted(true);
    }
  };

  const vonageToggleCamera = () => {
    if (vonagePublisher) {
      if (!vonageCameraMuted) {
        vonagePublisher.publishVideo(false);
        setVonageCameraMuted(true);
      } else if (currentState === 'preCall') {
        vonagePublisher.destroy();
        const element = document.getElementById('localVideoPreCall');
        if (element) element.innerHTML = '';
        const newPublisher = OT.initPublisher('localVideoPreCall', {
          height: '100%',
          width: '100%',
          showControls: false,
          publishAudio: false,
          publishVideo: true,
          insertMode: 'append' as const,
        }, (error: any) => {
          if (error) {
            if (error.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
              setCameraPermissionDenied(true);
            }
            console.error('Error reinitializing camera preview:', error);
          } else {
            setCameraPermissionDenied(false);
          }
        });
        setVonagePublisher(newPublisher);
        setVonageCameraMuted(false);
      } else {
        vonagePublisher.publishVideo(true);
        setVonageCameraMuted(false);
      }
    }
  };

  const vonageToggleBlur = () => {
    if (vonagePublisher) {
      if (vonageIsVideoBlurred) {
        vonagePublisher.clearVideoFilter();
        setVonageIsVideoBlurred(false);
      } else {
        vonagePublisher.applyVideoFilter(blurSettings);
        setVonageIsVideoBlurred(true);
      }
    }
  };

  const vonageRotateCamera = () => {
    if (vonagePublisher) {
      if (!isCameraRotated) {
        vonagePublisher.setVideoSource(backCameraId);
        const videoElement = document.querySelector('.OT_video-element') as HTMLElement;
        setIsCameraRotated(true);
        if (videoElement) {
          videoElement.style.transform = 'scaleX(1)';
        }
      } else {
        vonagePublisher.setVideoSource(frontCameraId);
        setIsCameraRotated(false);
        const videoElement = document.querySelector('.OT_video-element') as HTMLElement;
        if (videoElement) {
          videoElement.style.transform = 'scaleX(-1)';
        }
      }
    }
  };

  const makeCall = async () => {
    if (meetInfo.meetId) {
      // Use existing Vonage sessionId from preCall if available
      let vonageSessionIdToUse = vonageSessionId;

      // If no session exists, create one (fallback)
      if (!vonageSessionIdToUse) {
        try {
          const participantData = {
            visitorId: meetInfo.visitorId,
            meetId: meetInfo.meetId,
            accountId: meetInfo.accountId,
          };

          const sessionResponse = await createVonageSession(meetInfo.accountId, participantData);
          vonageSessionIdToUse = sessionResponse.sessionId || sessionResponse.session_id;

          if (vonageSessionIdToUse) {
            setVonageSessionId(vonageSessionIdToUse);
            const tokenResponse = await createVideoSessionToken(vonageSessionIdToUse, meetInfo.accountId);
            setVonageToken(tokenResponse);
          }
        } catch (error) {
          logger.error('Failed to create Vonage session', String(error));
        }
      }

      // Keep Telnyx roomId for WebRTC calling (not needed for Vonage provider)
      if (!isVonageProvider && !roomId) {
        await initRoom(meetInfo.meetId, meetInfo.accountId, null);
      }

      let remoteName = 'VideoApp';
      let sipState: SipStateProps = {
        roomId: roomId,
        accountId: meetInfo.accountId,
        widgetId: meetInfo.additionalFields.widget_id,
        widgetName: meetInfo.additionalFields.widgetName,
        url: window.location.href,
        visitorId: meetInfo.visitorId,
        sessionId: vonageSessionIdToUse,
        scheduleVideo: true,
      };

      if (meetInfo.additionalFields && meetInfo.additionalFields.trueengage_form) {
        sipState.trueengage_form = meetInfo.additionalFields.trueengage_form;
      }

      let phoneNumber = meetInfo.phoneNumber;
      if (!phoneNumber) {
        phoneNumber = '';
      }

      if (isVonageProvider) {
        sipState.phone = meetInfo.phoneNumber;
        sipState.remoteName = remoteName;
        sipState.type = 'video';

        console.log('[makeCall] vonageVoiceClient:', vonageVoiceClient);
        console.log('[makeCall] sipState:', sipState);

        try {
          await saveInteractionAttributes(meetInfo.accountId, meetInfo.visitorId, sipState);
          console.log('[makeCall] saveInteractionAttributes done');
          console.log('[makeCall] sipState: ' + JSON.stringify(sipState));
          const callId = await vonageVoiceClient?.serverCall(sipState);
          console.log('[makeCall] serverCall callId:', callId);
          vonageCallIdRef.current = callId ?? null;
        } catch (err) {
          console.error('[makeCall] error:', err);
        }
      } else {
        webRTCClient?.newCall({
          destinationNumber: phoneNumber,
          callerName: remoteName,
          video: false,
          clientState: btoa(JSON.stringify(sipState)),
        });
      }
    }
  };

  // Initialize Vonage session and publisher for preCall preview
  useEffect(() => {
    if (meetInfo.meetId && currentState === 'preCall' && cameraDevicesReady) {
      const initVonageForPreCall = async () => {
        try {
          const participantData = {
            visitorId: meetInfo.visitorId,
            meetId: meetInfo.meetId,
            accountId: meetInfo.accountId,
          };

          const sessionResponse = await createVonageSession(meetInfo.accountId, participantData);
          const sessionId = sessionResponse.sessionId || sessionResponse.session_id;

          if (sessionId) {
            setVonageSessionId(sessionId);
            const tokenResponse = await createVideoSessionToken(sessionId, meetInfo.accountId);
            setVonageToken(tokenResponse);

            // Wait for DOM element to be ready
            const checkElement = () => {
              const element = document.getElementById('localVideoPreCall');
              if (element) {
                initVonagePreview(sessionId, tokenResponse);
              } else {
                setTimeout(checkElement, 50);
              }
            };
            checkElement();
          }
        } catch (error) {
          logger.error('Failed to create Vonage session for preCall', String(error));
        }
      };

      initVonageForPreCall();
    }
  }, [meetInfo.meetId, currentState, cameraDevicesReady]);

  // Keep Telnyx roomId for WebRTC calling
  useEffect(() => {
    if (meetInfo.meetId) {
      initRoom(meetInfo.meetId, meetInfo.accountId, null);
    }
  }, [meetInfo]);

  useEffect(() => {
    initializeCameraDeviceIds();
  }, []);

  // When entering inCall, recreate publisher for inCall element
  useEffect(() => {
    if (currentState === 'inCall' && vonageSession && vonagePublisher && vonageSessionId && vonageToken) {
      const inCallElement = document.getElementById('localVideoInCall');
      if (inCallElement) {
        // Destroy old publisher from preCall
        if (vonagePublisher.element) {
          vonagePublisher.destroy();
        }

        // Clear preCall element
        const preCallElement = document.getElementById('localVideoPreCall');
        if (preCallElement) {
          preCallElement.innerHTML = '';
        }

        // Create new publisher for inCall element
        setTimeout(() => {
          const option = {
            height: '100%',
            width: '100%',
            showControls: false,
            publishAudio: !vonageMicrophoneMuted,
            publishVideo: true,
            insertMode: 'append' as const,
          };

          const newPublisher = OT.initPublisher('localVideoInCall', option, (error: any) => {
            if (error) {
              if (error.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
                setCameraPermissionDenied(true);
              }
              console.error('Error initializing publisher for inCall:', error);
            } else {
              setCameraPermissionDenied(false);
              console.log('Publisher initialized successfully for inCall');
              setVonagePublisher(newPublisher);

              // Publish the new publisher
              vonageSession.publish(newPublisher, (error: any) => {
                if (error) {
                  console.error('Error publishing in inCall:', error);
                } else {
                  console.log('Successfully published in inCall');
                }
              });
            }
          });
        }, 100);
      } else {
        // Fallback: initialize new Vonage session
        initVonage(vonageSessionId, vonageToken);
      }
    }
  }, [currentState, vonageSession, vonageSessionId, vonageToken]);

  // Initialize Vonage voice client (call signaling) when provider is 'vonage'
  useEffect(() => {
    if (!meetInfo.accountId || !isVonageProvider) return;

    console.log('Vonage provider');

    getVonageToken(meetInfo.accountId, { meetId: meetInfo.meetId }, async (data: any) => {
      try {
        console.log('[Vonage voice] token response:', data);
        const client = new VonageClient({ region: 'EU' });
        await client.createSession(data.token);
        console.log('[Vonage voice] session created');

        client.on('callHangup', (callId: string) => {
          if (callId === vonageCallIdRef.current) {
            vonageCallIdRef.current = null;
            setCurrentState('postCall');
          }
        });

        setVonageVoiceClient(client);
        console.log('[Vonage voice] client ready');
      } catch (error) {
        console.error('[Vonage voice] init failed:', error);
      }
    });
  }, [meetInfo.accountId, meetInfo.meetId, isVonageProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeCall) {
      activeCall.toggleAudioMute();
      activeCall.muteAudio();
    }
  }, [activeCall, webRTCClient]);

  const handleDisconnect = async () => {
    if (isVonageProvider) {
      if (vonageCallIdRef.current && vonageVoiceClient) {
        await vonageVoiceClient.hangup(vonageCallIdRef.current);
        vonageCallIdRef.current = null;
      }
    } else if (activeCall) {
      activeCall.hangup();
    }
    if (vonageSession) {
      vonageSession.disconnect();
      setVonageSession(null);
    }
    if (vonagePublisher) {
      vonagePublisher.destroy();
      setVonagePublisher(null);
    }
    if (room) {
      await room.disconnect();
    }
    try {
      await sendFinalStats();
    } catch (error) {
      console.error('Error sending final stats:', error);
    }
    setCurrentState('postCall');
  };

  if (fetchMeetInfoLoading) return <Loading />;
  if (fetchMeetInfoError) return <div>{fetchMeetInfoError}</div>;
  if (currentState === 'preCall')
    return (
      <>
        <PreCall
          changeCurrentState={changeCurrentState}
          meetInfo={meetInfo}
          localVideoTrack={null}
          localBlurredVideoTrack={null}
          localAudioTrack={null}
          makeCall={makeCall}
          microphoneMuted={vonageMicrophoneMuted}
          cameraMuted={vonageCameraMuted}
          toggleMicrophone={vonageToggleMicrophone}
          toggleCamera={vonageToggleCamera}
          availableDevices={availableDevices}
          selectedDevices={selectedDevices}
          updateDevice={updateDevice}
          applyBlurOnPreCallVideo={vonageToggleBlur}
          isVideoBlurred={vonageIsVideoBlurred}
          isUpgrade={isUpgrade}
          useVonage={true}
          cameraPermissionDenied={cameraPermissionDenied}
        />
      </>
    );

  if (currentState === 'inCall')
    return (
      <>
        <InCall
          changeCurrentState={changeCurrentState}
          localVideoTrack={null}
          localAudioTrack={null}
          remoteVideoTrack={null}
          remoteAudioTrack={null}
          activeCall={activeCall}
          room={null}
          microphoneMuted={vonageMicrophoneMuted}
          cameraMuted={vonageCameraMuted}
          toggleMicrophone={vonageToggleMicrophone}
          toggleCamera={vonageToggleCamera}
          availableDevices={availableDevices}
          selectedDevices={selectedDevices}
          updateDevice={updateDevice}
          isVideoBlurred={vonageIsVideoBlurred}
          applyBlurOnInCallVideo={vonageToggleBlur}
          TurnCameraOnInCallVideo={vonageRotateCamera}
          isUpgrade={isUpgrade}
          sendFinalStats={sendFinalStats}
          handleDisconnect={handleDisconnect}
          vonageSession={vonageSession}
          vonagePublisher={vonagePublisher}
          vonageRemoteStream={vonageRemoteStream}
          vonageVoiceClient={vonageVoiceClient}
          vonageCallId={vonageCallIdRef.current}
          callStatus={callStatus}
          cameraPermissionDenied={cameraPermissionDenied}
        />
      </>
    );

  if (currentState === 'postCall')
    return (
      <>
        <PostCall changeCurrentState={changeCurrentState} isVideoUpgrade={false} />
      </>
    );

  return null;
};

export default Video;
